import type { MouseEvent } from 'react';

import BpkBadge from '@skyscanner-internal/backpack-web/bpk-component-badge';
import BpkCard from '@skyscanner-internal/backpack-web/bpk-component-card';
import TrendUpIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/trend';
import TrendDownIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/trend--down';
import {
  BpkBackgroundImage,
  withLazyLoading,
  withLoadingBehavior,
  BORDER_RADIUS_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-image';
import {
  BpkBox,
  BpkFlex,
  BpkHStack,
  BpkSpacing,
  BpkVessel,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';
import BpkText, {
  TEXT_STYLES,
  TEXT_COLORS,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import { GLOBAL } from 'saddlebag-browser';

import { logMiniEvent } from '../../../logMiniEvent';
import { useMBDContext } from '../../common/MBD/useMBDContext';
import getDealsCommonTrackingProps from '../getDealsCommonTrackingProps';

import DealARIADescriptor from './DealARIADescriptor';
import Flight from './Flight';
import PriceButton from './PriceButton/PriceButton';

import type {
  DealCardData,
  FilteringTabDeal,
  LastUpdated,
  LegWithFormattedDate,
  PageTypeOptions,
  PriceBandStrings,
  PriceBandsDisplayState,
  TrackingContext,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/DealsEvolved';
import type { StringTranslations } from '@skyscanner-internal/landing-pages-shared-types/types-shared/components';

import STYLES from './DealCard.module.scss';

type DealCardProps = {
  strings: StringTranslations;
  trackingContext: TrackingContext;
  pageTypeOptions: PageTypeOptions;
  priceBandStrings?: PriceBandStrings;
  priceBandsDisplayState: PriceBandsDisplayState;
  showBadgePlaceholder?: boolean;
  tabName?: FilteringTabDeal['tabName'];
} & DealCardData;

const documentIfExists =
  typeof globalThis.window !== 'undefined' ? document : null;
const FadingLazyLoadedImage = withLoadingBehavior(
  withLazyLoading(BpkBackgroundImage, documentIfExists),
);

const onCardClicked = (props: {
  trackingContext: TrackingContext;
  pageTypeOptions: PageTypeOptions;
  deepLink: string;
}) => {
  logMiniEvent('DealsEvolvedEvent', 'deals_evolved_component', {
    deals_common: getDealsCommonTrackingProps(props),
    event_type: 'DEAL_CLICKED',
  });

  const globalWindow = GLOBAL.getWindow();
  globalWindow.setTimeout(() => {
    globalWindow.location.href = props.deepLink;
  }, 300);
};

const getLastUpdatedLegend = ({ highlight, text }: LastUpdated) => {
  if (highlight) {
    return <BpkBadge>{text}</BpkBadge>;
  }

  return (
    <BpkText textStyle={TEXT_STYLES.sm} color={TEXT_COLORS.textSecondary}>
      <BpkBox as="span" display="inline-block" paddingBlock={BpkSpacing.SM}>
        {text}
      </BpkBox>
    </BpkText>
  );
};

const getPriceBandLabel = (
  indicator: string,
  priceBandStrings?: PriceBandStrings,
): string => {
  if (!priceBandStrings) return '';

  switch (indicator) {
    case 'l':
      return priceBandStrings.low;
    case 't':
      return priceBandStrings.typical;
    case 'h':
      return priceBandStrings.high;
    default:
      return '';
  }
};

const getPriceBandBadgeClassName = (indicator: string): string => {
  switch (indicator) {
    case 'l':
      return STYLES.DealCard__priceBadgeLow;
    case 'h':
      return STYLES.DealCard__priceBadgeHigh;
    case 't':
    default:
      return STYLES.DealCard__priceBadgeTypical;
  }
};

const getPriceBandIcon = (indicator: string) => {
  if (indicator === 'l') {
    return (
      <TrendDownIcon
        className={`${STYLES.DealCard__badgeIcon} ${STYLES['DealCard__badgeIcon--low']}`}
        aria-hidden="true"
      />
    );
  }
  if (indicator === 'h') {
    return (
      <TrendUpIcon
        className={`${STYLES.DealCard__badgeIcon} ${STYLES['DealCard__badgeIcon--high']}`}
        aria-hidden="true"
      />
    );
  }
  return null;
};

const DealCard = ({
  cardIndex,
  dealCardTitle,
  deepLink,
  destination,
  direct,
  lastUpdated,
  legs,
  numberOfDealsRequested,
  numberOfDealsReturned,
  pageTypeOptions,
  price,
  priceBandIndicator,
  priceBandStrings = undefined,
  priceBandsDisplayState,
  showBadgePlaceholder = false,
  strings,
  tabName = undefined,
  trackingContext,
}: DealCardProps) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();
  const mbdComponentName = `${rawComponentName}_card`;
  const { countryName, image, name: destinationName } = destination;
  const { url } = image || {};

  let destinationClassName = STYLES.DealCard__destination;
  if (countryName) {
    destinationClassName = STYLES.DealCard__destinationWithCountryName;
  } else if (!url) {
    destinationClassName = `${STYLES.DealCard__destination} ${STYLES.DealCard__destinationWithoutCountryNameAndImage}`;
  }

  return (
    <BpkCard
      className={STYLES.DealCard__card}
      padded={false}
      href={deepLink}
      rel="nofollow"
      onClick={(e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        onCardClicked({ trackingContext, pageTypeOptions, deepLink });
      }}
      data-testid={`dealCard_${cardIndex}`}
      data-tracking-element-id={`${mbdComponentName}_${cardIndex}`}
      data-tracking-common-params={buildMBDParam(
        `${mbdComponentName}_${cardIndex}`,
        {
          number_of_deals_requested: numberOfDealsRequested
            ? numberOfDealsRequested.toString()
            : 'null',
          number_of_deals_returned: numberOfDealsReturned
            ? numberOfDealsReturned.toString()
            : 'null',
          price_bands_display_state: priceBandsDisplayState,
          ...(tabName ? { tab: tabName } : {}),
          ...(priceBandStrings
            ? { price_band_label: priceBandIndicator || 'none' }
            : {}),
        },
      )}
    >
      <DealARIADescriptor
        {...{ strings, legs, direct, price, city: destinationName }}
      />

      <BpkVessel
        aria-hidden="true"
        className={STYLES.DealCard__Image}
        style={{ display: 'flex', flex: '1 1 0%', flexDirection: 'column' }}
      >
        <BpkFlex grow={1} shrink={1} basis="0%" direction="column">
          {url && (
            <FadingLazyLoadedImage
              altText={destinationName}
              aspectRatio={315 / 128}
              src={url}
              borderRadiusStyle={BORDER_RADIUS_STYLES.sm}
              imageStyle={{
                width: '100%',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                backgroundSize: 'cover',
                overflow: 'hidden',
                backgroundImage: `url(${url})`,
                borderRadius: '.375rem .375rem 0 0',
              }}
            />
          )}
          <BpkVessel
            className={[
              url
                ? STYLES.DealCard__mainBlockWithImage
                : STYLES.DealCard__mainBlock,
              priceBandStrings &&
                (url
                  ? STYLES['DealCard__mainBlockWithImage--withPriceBands']
                  : STYLES['DealCard__mainBlock--withPriceBands']),
            ]
              .filter(Boolean)
              .join(' ')}
            style={{ display: 'flex', flex: '1 1 0%', flexDirection: 'column' }}
          >
            <BpkFlex grow={1} shrink={1} basis="0%" direction="column">
              {priceBandStrings &&
              (priceBandIndicator || showBadgePlaceholder) ? (
                <BpkBox marginBottom={BpkSpacing.MD}>
                  {priceBandIndicator ? (
                    <BpkBadge
                      type="normal"
                      className={getPriceBandBadgeClassName(priceBandIndicator)}
                      data-testid="price-band-indicator"
                    >
                      {getPriceBandIcon(priceBandIndicator)}
                      {getPriceBandLabel(priceBandIndicator, priceBandStrings)}
                    </BpkBadge>
                  ) : (
                    // Placeholder uses 'l' (low) styling — this component only shows
                    // placeholders in the EnableFlightDealsDealPriceBandsLow variant
                    // where low is the only badge type that appears.
                    <BpkBadge
                      type="normal"
                      className={`${STYLES.DealCard__priceBadgeLow} ${STYLES.DealCard__priceBandBadgePlaceholder}`}
                      aria-hidden="true"
                    >
                      {getPriceBandLabel('l', priceBandStrings)}
                    </BpkBadge>
                  )}
                </BpkBox>
              ) : null}
              {dealCardTitle && (
                <BpkText
                  textStyle={TEXT_STYLES.heading4}
                  className={destinationClassName}
                  data-testid="deal-from-to-city-names"
                >
                  {dealCardTitle}
                </BpkText>
              )}
              {countryName && (
                <BpkText
                  color={TEXT_COLORS.textSecondary}
                  textStyle={TEXT_STYLES.sm}
                  data-testid="deal-country-name"
                  className={STYLES.DealCard__countryName}
                >
                  {countryName}
                </BpkText>
              )}
              {(legs || []).map((leg: LegWithFormattedDate) => (
                <Flight
                  key={JSON.stringify(leg)}
                  leg={leg}
                  strings={strings}
                  direct={direct}
                />
              ))}
              <BpkHStack
                marginTop="auto"
                justify="space-between"
                align="center"
                gap={BpkSpacing.LG}
              >
                {lastUpdated && getLastUpdatedLegend(lastUpdated)}
                <PriceButton strings={strings} price={price} />
              </BpkHStack>
            </BpkFlex>
          </BpkVessel>
        </BpkFlex>
      </BpkVessel>
    </BpkCard>
  );
};

export default DealCard;
